/**
 * 音楽再生のファサード
 *
 * 「要素再生エンジン（HTMLAudioElement）」と「ミックス再生エンジン（Web Audio）」の
 * どちらに再生を任せるかを一元管理する。切替は必ずここを通すことで、
 * 2 つのエンジンが同時に音を出す・どちらも鳴らないといった状態を防ぐ。
 *
 * どちらを使うかは music-stem-plan.ts の判定に従う。
 * 音量が振り切っている組み合わせは分離済みファイル 1 本で代用できるため要素再生を選び、
 * iOS のバックグラウンド再生を維持する。
 */

import { toast } from 'sonner'

import { useMusicStore } from '@/store/useMusicStore'

import {
  activateElementEngine,
  deactivateElementEngine,
  disposeMusicAudioEngine,
  getPlaybackTime,
  initMusicAudioEngine,
} from './music-audio-engine'
import { peekSigned, resolveSigned, streamPath, withStem } from './music-signed-url'
import {
  activateMixEngine,
  deactivateMixEngine,
  disposeMixEngine,
  getMixPosition,
  setMixFailureHandler,
} from './music-stem-engine'
import { isSameEngineTarget, resolveStemPlan, type StemPlan } from './music-stem-plan'

const ORIGINAL_PLAN: StemPlan = { engine: 'element', stem: null }

/** 現在どちらのエンジンが鳴らしているか */
let activePlan: StemPlan = ORIGINAL_PLAN

/**
 * 最後に「こうしたい」と決めた再生方式。
 * activePlan は切替が終わるまで古いままなので、判定にはこちらを使う。
 * これが無いと、切替中に走ったストア更新が同じ切替をもう一度始めてしまう。
 */
let desiredPlan: StemPlan = ORIGINAL_PLAN

/** 切替の世代番号。切替中に曲送りやトグル連打が入った古い処理を捨てる */
let switchGeneration = 0

/** ミックス再生の注意書きを出したか（セッション中 1 回だけ） */
let mixNoticeShown = false

let unsubscribeStore: (() => void) | null = null

/** エピソードごとの分離済みファイルの有無。null は判定中 */
const availability = new Map<number, boolean>()

/** 判定リクエストの重複をまとめる */
const availabilityInFlight = new Map<number, Promise<boolean>>()

const currentEpisodeId = (): number | null => {
  const { queue, currentIndex } = useMusicStore.getState()
  return queue.at(currentIndex)?.episodeId ?? null
}

// ============================================================
// 分離済みファイルの有無
// ============================================================

const fetchAvailability = async (episodeId: number): Promise<boolean> => {
  const path = streamPath(episodeId)
  const url = peekSigned(path) ?? (await resolveSigned(path))
  if (!url) return false

  try {
    const res = await fetch(withStem(url, 'vocals'), { method: 'HEAD' })
    return res.ok
  } catch {
    // 通信エラーは「無い」とは断定できないが、鳴らせない点では同じ
    return false
  }
}

const resolveAvailability = (episodeId: number): Promise<boolean> => {
  const cached = availability.get(episodeId)
  if (cached !== undefined) return Promise.resolve(cached)

  const pending = availabilityInFlight.get(episodeId)
  if (pending) return pending

  const task = fetchAvailability(episodeId)
    .then((available) => {
      availability.set(episodeId, available)
      return available
    })
    .finally(() => {
      availabilityInFlight.delete(episodeId)
    })

  availabilityInFlight.set(episodeId, task)
  return task
}

/** 現在の曲のステム有無をストアへ反映する（判定済みなら即座に、未判定なら取得後に） */
const syncAvailability = async (): Promise<void> => {
  const episodeId = currentEpisodeId()
  if (episodeId === null) {
    useMusicStore.getState().setStemsAvailable(false)
    return
  }

  const available = await resolveAvailability(episodeId)
  // 判定を待っている間に曲が変わっていたら捨てる
  if (currentEpisodeId() !== episodeId) return
  useMusicStore.getState().setStemsAvailable(available)
}

// ============================================================
// エンジンの排他切替
// ============================================================

const targetPlan = (): StemPlan => {
  const { stemEnabled, vocalVolume, instVolume, stemsAvailable } = useMusicStore.getState()
  return resolveStemPlan({ enabled: stemEnabled, vocalVolume, instVolume, stemsAvailable })
}

/** 現在鳴っているエンジンを止めて、再生位置を返す */
const releaseActive = (): number =>
  activePlan.engine === 'element' ? deactivateElementEngine() : deactivateMixEngine()

const activatePlan = async (plan: StemPlan, position: number): Promise<boolean> => {
  if (plan.engine === 'element') {
    await activateElementEngine(plan.stem, position)
    return true
  }
  return activateMixEngine(position)
}

const switchTo = async (plan: StemPlan): Promise<void> => {
  const generation = ++switchGeneration
  const store = useMusicStore.getState()
  desiredPlan = plan
  store.setStemSwitching(true)

  // 先に完全に手放してから次を用意する。重ねると一瞬二重に鳴る
  const position = releaseActive()
  activePlan = plan

  const ok = await activatePlan(plan, position)

  // 切替中にさらに切替が始まっていたら、こちらの後始末はしない
  if (generation !== switchGeneration) return

  if (!ok) {
    // 分離済みファイルが無い等で鳴らせなかった。原曲に戻して聴ける状態を保つ
    const resumeAt = releaseActive()
    desiredPlan = ORIGINAL_PLAN
    activePlan = ORIGINAL_PLAN
    availability.set(currentEpisodeId() ?? -1, false)
    // ここでストアが更新されるが、desiredPlan が原曲なので再切替は走らない
    store.setStemsAvailable(false)
    await activateElementEngine(null, resumeAt)
    toast.error('この曲はボーカル分離に対応していません')
  }

  if (generation !== switchGeneration) return
  store.setStemSwitching(false)

  if (!ok || plan.engine !== 'mix' || mixNoticeShown) return
  // 中間音量は Web Audio で混ぜるため、バックグラウンドでは続かない
  mixNoticeShown = true
  toast.info('この音量設定ではバックグラウンド再生が止まります', {
    description: 'iPhone は消音スイッチがオンだと音が出ません。ボーカルかインストを 0% にすると通常再生に戻ります',
  })
}

/** ストアの状態から必要な再生方式を決め、変わっていれば切り替える */
const applyPlan = (): void => {
  const target = targetPlan()
  if (isSameEngineTarget(target, desiredPlan)) {
    // 音量だけの変化は各エンジンが gain / そのままで処理する
    desiredPlan = target
    activePlan = target
    return
  }
  void switchTo(target)
}

// ============================================================
// 初期化
// ============================================================

const handleMixFailure = (): void => {
  const episodeId = currentEpisodeId()
  if (episodeId !== null) availability.set(episodeId, false)
  // ストア更新で applyPlan が走る。走らなかった場合に備えて明示的にも呼ぶ（二重実行は無害）
  useMusicStore.getState().setStemsAvailable(false)
  applyPlan()
}

const subscribeStore = (): void => {
  unsubscribeStore = useMusicStore.subscribe((state, prev) => {
    const episodeId = state.queue.at(state.currentIndex)?.episodeId
    const prevEpisodeId = prev.queue.at(prev.currentIndex)?.episodeId

    if (episodeId !== prevEpisodeId) {
      void syncAvailability().then(applyPlan)
      return
    }

    if (
      state.stemEnabled !== prev.stemEnabled ||
      state.vocalVolume !== prev.vocalVolume ||
      state.instVolume !== prev.instVolume ||
      state.stemsAvailable !== prev.stemsAvailable
    ) {
      applyPlan()
    }
  })
}

/** 再生エンジン群を初期化する（`/music` 配下に入ったときに呼ぶ） */
export const initMusicPlayback = (): void => {
  initMusicAudioEngine()
  setMixFailureHandler(handleMixFailure)
  activePlan = ORIGINAL_PLAN
  desiredPlan = ORIGINAL_PLAN
  if (!unsubscribeStore) subscribeStore()

  // 復帰直後にステムが有効なら、有無を確かめてから必要な方式へ切り替える
  void syncAvailability().then(applyPlan)
}

/** 再生エンジン群を破棄する（`/music` 配下から離れたときと HMR 時に呼ぶ） */
export const disposeMusicPlayback = (): void => {
  unsubscribeStore?.()
  unsubscribeStore = null
  setMixFailureHandler(null)
  switchGeneration += 1
  disposeMixEngine()
  disposeMusicAudioEngine()
  activePlan = ORIGINAL_PLAN
  desiredPlan = ORIGINAL_PLAN
  useMusicStore.getState().setStemSwitching(false)
}

/** 歌詞のカラオケ表示などが使う、現在の再生位置（秒） */
export const getMusicPlaybackTime = (): number | null =>
  activePlan.engine === 'element' ? getPlaybackTime() : getMixPosition()

if (import.meta.hot) {
  import.meta.hot.dispose(disposeMusicPlayback)
}
